import type { ReactElement } from "react"
import styled, { type DefaultTheme } from "styled-components"

type Tone = "muted" | "error" | "success"

/**
 * One entry per tone, so the surface can never drift from the text colour and
 * so the live region matches the urgency. A confirmation is `status` and not
 * `alert`: `alert` interrupts whatever a screen reader is saying, which is right
 * for a failure and rude for a success the customer just asked for.
 */
const TONES = {
  muted: { text: "textSecondary", surface: "surfaceSubtle", role: undefined },
  error: { text: "error", surface: "errorSurface", role: "alert" },
  success: { text: "success", surface: "successSurface", role: "status" },
} as const satisfies Record<
  Tone,
  { text: keyof DefaultTheme["color"]; surface: keyof DefaultTheme["color"]; role: string | undefined }
>

const Root = styled.p<{ $tone: Tone }>`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme, $tone }) => theme.color[TONES[$tone].text]};
  background-color: ${({ theme, $tone }) => theme.color[TONES[$tone].surface]};
`

interface StateMessageProps {
  readonly children: string
  readonly tone?: Tone
  readonly qaHook: string
}

export const StateMessage = ({ children, tone = "muted", qaHook }: StateMessageProps): ReactElement => (
  <Root $tone={tone} data-qa-hook={qaHook} role={TONES[tone].role}>
    {children}
  </Root>
)
